import React, { useEffect, useState } from 'react';

import { CITY_KEY, FILTER_TYPE_DYNAMIC, STATE_KEY } from '#/utils';
import { AppliedFilters, MainFilterBlock, MoreFiltersDropdown } from '.';

const Filters = ({ filtersData, queryParams, setQueryParams, onClearFilters, addCompany = false }) => {
    const [geoData, setGeoData] = useState({
        statesByCountry: {},
        citiesByState: {},
    });

    const { filters, moreFilters } = filtersData || {};

    const handleFilterChange = (name, value) => {
        setQueryParams(prev => ({ ...prev, [name]: value }));
    };

    const handleFilterRemove = name => {
        setQueryParams(prev => {
            const newParams = { ...prev };
            delete newParams[name];
            return newParams;
        });
    };

    const hasCountryFilter = filters?.some(f => f.name === 'country');

    const enrichedMoreFilters = hasCountryFilter
        ? [
              { name: STATE_KEY, label: 'State', type: FILTER_TYPE_DYNAMIC },
              { name: CITY_KEY, label: 'City', type: FILTER_TYPE_DYNAMIC },
              ...(moreFilters || []),
          ]
        : moreFilters || [];
    const loadCountryStateCity = async () => {
        try {
            return await import('country-state-city');
        } catch (e) {
            console.error('Failed to load country-state-city:', e);
            return { Country: [], State: [], City: [] };
        }
    };

    useEffect(() => {
        const loadStates = async () => {
            const countries = queryParams.country || [];
            if (countries?.length === 0) {
                setGeoData(prev => ({ ...prev, statesByCountry: {} }));
                return;
            }

            const { Country, State } = await loadCountryStateCity();
            const statesMap = {};

            countries.forEach(cc => {
                const country = Country.getAllCountries().find(c => c.name === cc);
                const isoCode = country?.isoCode;
                if (isoCode) {
                    const states = State.getStatesOfCountry(isoCode);
                    statesMap[cc] = states;
                }
            });

            setGeoData(prev => ({ ...prev, statesByCountry: statesMap }));
        };

        loadStates();
    }, [queryParams?.country]);

    useEffect(() => {
        const loadCities = async () => {
            const selectedCountries = queryParams.country || [];
            const selectedStates = queryParams.state || [];

            if (!selectedCountries.length || !selectedStates.length) {
                setGeoData(prev => ({ ...prev, citiesByState: {} }));
                return;
            }

            const { Country, State, City } = await loadCountryStateCity();

            const countryMap = Country.getAllCountries().reduce((acc, c) => {
                acc[c.name] = c.isoCode;
                return acc;
            }, {});

            const newCityMap = {};

            selectedCountries.forEach(countryName => {
                const countryIso = countryMap[countryName];
                if (!countryIso) return;

                const statesOfCountry = State.getStatesOfCountry(countryIso);
                const stateIsoMap = statesOfCountry.reduce((acc, s) => {
                    acc[s.name] = s.isoCode;
                    return acc;
                }, {});

                selectedStates.forEach(stateName => {
                    const stateIso = stateIsoMap[stateName];
                    if (!stateIso) return;

                    const cities = City.getCitiesOfState(countryIso, stateIso).map(city => ({
                        id: `${stateIso}-${city.name}`,
                        name: city.name,
                    }));
                    newCityMap[stateName] = cities;
                });
            });

            setGeoData(prev => ({ ...prev, citiesByState: newCityMap }));
        };

        loadCities();
    }, [queryParams.country, queryParams.state]);

    return (
        <>
            <div className='mb-4 flex flex-wrap gap-4 items-center py-1 px-5'>
                <MainFilterBlock
                    filters={filters || []}
                    queryParams={queryParams}
                    handleFilterChange={handleFilterChange}
                    setQueryParams={setQueryParams}
                    addCompany={addCompany}
                />
                <MoreFiltersDropdown
                    moreFilters={enrichedMoreFilters}
                    queryParams={queryParams}
                    handleFilterChange={handleFilterChange}
                    setQueryParams={setQueryParams}
                    geoData={geoData}
                />
            </div>

            <AppliedFilters
                queryParams={queryParams}
                handleFilterRemove={handleFilterRemove}
                handleFilterChange={handleFilterChange}
                onClearFilters={onClearFilters}
                moreFiltersConfig={enrichedMoreFilters}
            />
        </>
    );
};

export { Filters };
